import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { SEOHead } from '../components/SEOHead';
import { FAQSection } from '../components/FAQSection';
import { trackPageView } from '../utils/analytics';

export const CoreWebVitalsCheck = () => {
  useEffect(() => {
    trackPageView('/core-web-vitals-check');
  }, []);

  const faqs = [
    {
      question: "What are Core Web Vitals?",
      answer: "Core Web Vitals are the three page experience signals Google recommends for measuring real-world user experience: Largest Contentful Paint (LCP), Interaction to Next Paint (INP), and Cumulative Layout Shift (CLS). LCP measures loading speed, INP measures interaction responsiveness, and CLS measures visual stability. Google uses Core Web Vitals in its ranking systems."
    },
    {
      question: "Why do Core Web Vitals matter for SEO?",
      answer: "Core Web Vitals matter because they describe whether a page feels fast, responsive, and stable for real users. Google uses Core Web Vitals in ranking systems, but good scores do not guarantee top rankings. They are most valuable when paired with helpful content, strong technical SEO, and a clean user experience."
    },
    {
      question: "What is a good LCP score?",
      answer: "A good Largest Contentful Paint (LCP) score is 2.5 seconds or less. LCP between 2.5 s and 4.0 s needs improvement, and anything above 4.0 s is poor. LCP measures how quickly the largest visible element — typically a hero image or heading — loads in the viewport. It is the primary Lighthouse Core Web Vitals loading metric."
    },
    {
      question: "What is a good INP score?",
      answer: "A good Interaction to Next Paint (INP) score is 200 milliseconds or less. INP between 200 ms and 500 ms needs improvement, and anything above 500 ms is poor. INP replaced First Input Delay (FID) in March 2024. INP optimisation typically involves reducing long JavaScript tasks, deferring non-critical scripts, and using web workers for heavy computation."
    },
    {
      question: "How can I improve my CLS score?",
      answer: "To improve Cumulative Layout Shift (CLS), always declare explicit width and height attributes on images and videos, reserve space for ads and third-party embeds, avoid inserting content above existing content after load, and use CSS transform animations rather than animating properties that trigger layout changes. A CLS score of 0.1 or below is considered good by Google."
    },
    {
      question: "Does this tool measure INP directly?",
      answer: "Not in the main result cards today. The current analysis surfaces LCP, CLS, and Lighthouse responsiveness signals such as TBT, then uses contextual recommendations to help teams work on the responsiveness issues that also affect INP in real-world usage."
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Core Web Vitals Check — LCP, INP & CLS Analyser | Web Speed Advisor"
        description="Core Web Vitals checker for LCP, INP, and CLS. See your page experience signals, understand Google's thresholds, and get actionable steps to optimise your site."
        canonical="https://webspeedadvisor.com/core-web-vitals-check"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Web Speed Advisor Core Web Vitals Check',
          applicationCategory: 'DeveloperApplication',
          operatingSystem: 'Web',
          url: 'https://webspeedadvisor.com/core-web-vitals-check',
          description: 'Core Web Vitals checker for LCP, INP, CLS, Lighthouse responsiveness signals, and AI-powered page speed optimisation recommendations.',
          offers: {
            '@type': 'Offer',
            price: '0',
            priceCurrency: 'USD'
          }
        }}
      />

      <div className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-black font-chivo mb-6">
            Core Web Vitals Checker
          </h1>
          <p className="text-xl text-slate-300 mb-8">
            Analyse LCP, INP, and CLS for any URL. Understand Google's page experience signals and get prioritised steps to optimise your site's performance.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-primary hover:bg-primary-hover text-white font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Check My Core Web Vitals
          </Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-16 space-y-12">
        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            Understanding Core Web Vitals
          </h2>
          <div className="prose prose-lg max-w-none text-slate-700">
            <p>
              Core Web Vitals are three standardised page experience signals that Google measures in the real world using
              Chrome user data. They cover loading performance (LCP), interaction responsiveness (INP), and visual
              stability (CLS). Google uses these signals in its ranking systems, which makes them useful for both
              search visibility and user experience work.
            </p>
            <p>
              Unlike synthetic lab metrics, Core Web Vitals reflect how actual visitors experience your pages. A slow
              LCP means users wait too long to see your main content. A high INP score means the page feels unresponsive
              to clicks and taps. Poor CLS causes visible elements to jump unexpectedly, leading to accidental clicks
              and a frustrating experience. Optimising all three improves real-world user satisfaction and supports
              stronger page experience signals for organic search.
            </p>
            <p>
              In the current Web Speed Advisor product, the main analysis experience surfaces LCP, CLS, and Lighthouse responsiveness signals such as TBT. That makes this page best used as a practical guide to Core Web Vitals concepts and the kinds of fixes that improve them.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-8">
            The Three Core Web Vitals Explained
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Largest Contentful Paint (LCP)</h3>
              <p className="text-slate-700 mb-3">
                LCP is the primary Lighthouse Core Web Vitals loading metric. It measures the time from when a page
                begins loading to when the largest visible content element — typically a hero image, banner, or
                above-the-fold heading — is fully rendered in the viewport. A fast LCP signals to users that the page
                is useful and loading correctly. This is the metric most directly improved by server response time,
                image optimisation, and render-blocking resource elimination.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-semibold mb-2">LCP Thresholds:</p>
                <ul className="text-sm space-y-1">
                  <li className="text-green-600">✓ Good: 2.5 seconds or less</li>
                  <li className="text-orange-600">! Needs Improvement: 2.5 s – 4.0 s</li>
                  <li className="text-red-600">✗ Poor: Above 4.0 seconds</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Interaction to Next Paint (INP)</h3>
              <p className="text-slate-700 mb-3">
                INP replaced First Input Delay (FID) as a Core Web Vital in March 2024. It measures the time between
                a user interaction — click, tap, or keyboard input — and the next visual update that the browser
                paints in response. INP reflects the overall responsiveness of a page across the entire session, not
                just the first interaction. INP optimisation focuses on reducing long JavaScript tasks and minimising
                main-thread blocking work.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-semibold mb-2">INP Thresholds:</p>
                <ul className="text-sm space-y-1">
                  <li className="text-green-600">✓ Good: 200 milliseconds or less</li>
                  <li className="text-orange-600">! Needs Improvement: 200 ms – 500 ms</li>
                  <li className="text-red-600">✗ Poor: Above 500 milliseconds</li>
                </ul>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Cumulative Layout Shift (CLS)</h3>
              <p className="text-slate-700 mb-3">
                CLS is the Core Web Vitals visual stability metric. It quantifies the total amount of unexpected layout
                shift that occurs during the lifespan of a page. A layout shift happens when a visible element moves
                from one position to another without being triggered by a user interaction. Common causes include
                images without declared dimensions, dynamically injected content, and web fonts that cause text to
                reflow on load. A CLS test score of 0.1 or below indicates good visual stability.
              </p>
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-semibold mb-2">CLS Thresholds:</p>
                <ul className="text-sm space-y-1">
                  <li className="text-green-600">✓ Good: 0.1 or less</li>
                  <li className="text-orange-600">! Needs Improvement: 0.1 – 0.25</li>
                  <li className="text-red-600">✗ Poor: Above 0.25</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-3xl font-bold font-chivo text-slate-900 mb-6">
            How to Improve Your Core Web Vitals
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Improving LCP</h3>
              <ul className="space-y-2 text-slate-700">
                <li>• Optimise and compress images using modern formats (WebP, AVIF)</li>
                <li>• Preload the LCP image element using a &lt;link rel="preload"&gt; tag</li>
                <li>• Remove render-blocking JavaScript and CSS from the critical path</li>
                <li>• Reduce server response time (Time to First Byte) with better hosting or caching</li>
                <li>• Serve assets via a Content Delivery Network (CDN) close to your users</li>
                <li>• Avoid lazy-loading the LCP element — it should load eagerly</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Improving INP</h3>
              <ul className="space-y-2 text-slate-700">
                <li>• Break up long JavaScript tasks into smaller chunks using scheduler.yield()</li>
                <li>• Defer non-critical scripts until after the page is interactive</li>
                <li>• Use web workers to move heavy computation off the main thread</li>
                <li>• Minimise third-party JavaScript that blocks interaction handling</li>
                <li>• Remove unused JavaScript to reduce parse and execution time</li>
                <li>• Implement code splitting and route-based lazy loading</li>
              </ul>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900 mb-3">Improving CLS</h3>
              <ul className="space-y-2 text-slate-700">
                <li>• Always declare explicit width and height attributes on images and videos</li>
                <li>• Reserve space for ad slots and third-party embeds using aspect-ratio CSS</li>
                <li>• Avoid inserting content above existing content after the initial load</li>
                <li>• Use CSS transform animations rather than animating layout-triggering properties</li>
                <li>• Preload web fonts and use font-display: swap to prevent invisible-text shifts</li>
                <li>• Use font-size-adjust to reduce reflow when custom fonts replace system fonts</li>
              </ul>
            </div>
          </div>
        </section>

        <FAQSection faqs={faqs} />

        <section className="bg-primary text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold font-chivo mb-4">
            Ready to Check Your Core Web Vitals?
          </h2>
          <p className="text-lg mb-6 text-blue-100">
            Get a full LCP, INP, and CLS analysis with prioritised optimisation steps — free, no sign-up required.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-bold px-8 py-4 rounded-full transition-all"
          >
            <Zap size={24} />
            Start Free Check
          </Link>
        </section>
      </div>
    </div>
  );
};

export default CoreWebVitalsCheck;
